/**
 * Main category landing page — route: `/:industryId` (e.g. `/legal`).
 *
 * Layout order (top → bottom):
 *   1. Hero + search
 *   2. Topic hub grid (filtered by search — sits directly under hero)
 *   3. Category story, partner strip, subcategory showcase, FAQs, CTA
 */
import React, { useCallback, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { useSelector } from "react-redux";
import {
  ArrowRight,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  HelpCircle,
  Image as ImageIcon,
  LayoutGrid,
  Link2,
  MessageCircle,
  Play,
  Search,
  Sparkles,
  TrendingUp,
  UserCircle,
  Users,
} from "lucide-react";
import { INDUSTRIES, getItemsByIndustry } from "../../data/categoryData";
import { useCategories } from "../../contexts/CategoriesContext";
import { getCategoryPageTheme } from "../../data/categoryPageThemes";
import {
  getIndustryVisual,
  getSponsorForIndustry,
  getSubcategoryShowcaseImage,
  getTopicArticles,
} from "../../data/mockContentData";
import Footer from "../../components/footer/Footer";
import { useSignupCta } from "../../hooks/useSignupCta";
import BettingGamingCategoryPageSections from "../../components/category/BettingGamingCategoryPageSections";
import { BETTING_GAMING_POPULAR_QUESTIONS } from "../../data/bettingGamingCategoryPageContent";

const importIcon = (name) => {
  try {
    const mod = require("lucide-react");
    return mod[name] || mod.Star;
  } catch {
    return Sparkles;
  }
};

const patternUid = (industryId) => industryId.replace(/[^a-z0-9]/gi, "");

/** Full-bleed decorative layer — one motif per category family. */
const HeroPattern = ({ patternId, industryId }) => {
  const uid = patternUid(industryId);
  if (patternId === "blueprint") {
    return (
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full opacity-50"
        aria-hidden
      >
        <defs>
          <pattern
            id={`${uid}-bp`}
            width="44"
            height="44"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M44 0H0V44"
              fill="none"
              stroke="rgba(120,80,40,0.18)"
              strokeWidth="0.7"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${uid}-bp)`} />
      </svg>
    );
  }
  if (patternId === "columns") {
    return (
      <div
        className="pointer-events-none absolute inset-0 flex justify-evenly opacity-[0.14]"
        aria-hidden
      >
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="w-px bg-gradient-to-b from-transparent via-[#93c5fd] to-transparent"
          />
        ))}
      </div>
    );
  }
  if (patternId === "organic") {
    return (
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden
      >
        <div className="absolute -left-20 top-10 h-72 w-72 rounded-full bg-emerald-300/35 blur-3xl" />
        <div className="absolute -right-16 bottom-0 h-80 w-80 rounded-full bg-lime-300/30 blur-3xl" />
        <div className="absolute left-1/3 top-1/2 h-48 w-48 -translate-y-1/2 rounded-full bg-teal-200/25 blur-2xl" />
      </div>
    );
  }
  if (patternId === "pulse") {
    return (
      <svg
        className="pointer-events-none absolute inset-x-0 bottom-0 h-32 w-full opacity-30 sm:h-40"
        viewBox="0 0 1200 120"
        preserveAspectRatio="none"
        aria-hidden
      >
        <path
          d="M0 60 L80 60 L120 20 L160 90 L220 35 L280 85 L340 55 L420 95 L520 40 L620 75 L740 25 L880 70 L980 45 L1060 80 L1120 50 L1200 60"
          fill="none"
          stroke="rgba(220,38,38,0.55)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  if (patternId === "orbit") {
    return (
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.12]"
        aria-hidden
      >
        <div className="absolute h-[min(100vw,48rem)] w-[min(100vw,48rem)] rounded-full border-2 border-dashed border-purple-500" />
        <div className="absolute h-[min(80vw,36rem)] w-[min(80vw,36rem)] rounded-full border border-purple-400" />
        <div className="absolute h-[min(55vw,24rem)] w-[min(55vw,24rem)] rounded-full border border-fuchsia-400/70" />
      </div>
    );
  }
  if (patternId === "arena") {
    return (
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden opacity-40"
        aria-hidden
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "repeating-linear-gradient(-28deg, transparent, transparent 12px, rgba(249,115,22,0.09) 12px, rgba(249,115,22,0.09) 13px)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-orange-500/10" />
      </div>
    );
  }
  return null;
};

const CategoryStoryBand = ({ theme, industryLabel, color, narrativeDark }) => (
  <motion.section
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    className={`relative z-10 mx-auto mt-4 max-w-8xl overflow-hidden rounded-[2rem] px-6 py-10 sm:px-10 sm:py-12 ${theme.narrativeSectionClass}`}
  >
    <div
      className="pointer-events-none absolute -right-24 top-0 h-64 w-64 rounded-full opacity-50 blur-3xl"
      style={{ background: color }}
      aria-hidden
    />
    <div className="relative grid gap-10 lg:grid-cols-[1.15fr_0.85fr]">
      <div>
        <p
          className={`text-xs font-black uppercase tracking-[0.22em] ${theme.narrativeKickerClass}`}
        >
          About this category
        </p>
        <h2
          className={`mt-3 text-2xl leading-tight sm:text-4xl ${theme.fontHeadingClass} ${narrativeDark ? "text-white" : "text-gray-950"}`}
        >
          {theme.narrativeTitle}
        </h2>
        <p
          className={`mt-5 text-base leading-8 sm:text-lg ${narrativeDark ? "text-slate-300" : theme.heroMutedClass}`}
        >
          {theme.editorialIntro}
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {theme.narrativePillars.map((p) => (
            <div
              key={p.title}
              className={
                narrativeDark
                  ? "rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm"
                  : "rounded-2xl border border-black/[0.06] bg-white/70 p-4 backdrop-blur-sm"
              }
            >
              <p
                className={`text-sm font-black ${narrativeDark ? "text-white" : "text-gray-950"}`}
              >
                {p.title}
              </p>
              <p
                className={`mt-2 text-xs leading-relaxed ${narrativeDark ? "text-white/65" : "text-gray-600"}`}
              >
                {p.text}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col justify-between gap-6">
        <blockquote
          className={`rounded-2xl border p-6 text-lg font-serif italic leading-relaxed ${narrativeDark ? "border-white/15 bg-white/5 text-white" : "border-black/[0.07] bg-black/[0.03] text-gray-900"}`}
        >
          {theme.pullQuote}
        </blockquote>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-1">
          {theme.statStrip.map((s) => (
            <div
              key={s.label}
              className={`flex flex-col rounded-2xl px-4 py-3 ${narrativeDark ? "border border-white/10" : "border border-black/[0.06]"}`}
            >
              <span
                className={`text-[10px] font-black uppercase tracking-[0.18em] ${narrativeDark ? "text-white/45" : "text-gray-500"}`}
              >
                {s.label}
              </span>
              <span
                className={`mt-1 text-xs font-semibold ${narrativeDark ? "text-white/75" : "text-gray-800"}`}
              >
                {s.hint}
              </span>
            </div>
          ))}
        </div>
        <p
          className={`text-xs font-bold uppercase tracking-[0.14em] ${narrativeDark ? "text-slate-400" : theme.heroMutedClass}`}
        >
          You are browsing · {industryLabel}
        </p>
      </div>
    </div>
  </motion.section>
);

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ImageCollage = ({
  visual,
  color,
  industry,
  frameClassName = "",
  topicsCount = 10,
  guidesApprox = "30+",
  heroIsDark = false,
}) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.97, y: 20 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    className="relative mx-auto w-full max-w-md lg:mx-0 lg:max-w-none"
  >
    <div
      className="absolute -inset-6 rounded-[2.25rem] opacity-[0.35] blur-3xl"
      style={{
        background: `linear-gradient(135deg, ${color}55, transparent 55%)`,
      }}
      aria-hidden
    />
    <div
      className={`relative overflow-hidden rounded-[1.75rem] p-[6px] ring-1 ${
        heroIsDark
          ? "bg-gradient-to-b from-slate-900 to-slate-950 ring-white/15 shadow-[0_36px_80px_-38px_rgba(0,0,0,0.88)]"
          : "bg-gradient-to-b from-white to-neutral-100/90 shadow-[0_28px_70px_-30px_rgba(0,0,0,0.42),inset_0_1px_0_rgba(255,255,255,0.65)] ring-black/[0.05]"
      } ${frameClassName}`}
    >
      <div
        className={`group rounded-[1.35rem] p-1 ${heroIsDark ? "bg-slate-800/95 ring-1 ring-white/10" : "bg-neutral-200/80"}`}
      >
        <div
          className={`flex items-center justify-between gap-3 border-b px-3 py-2.5 ${heroIsDark ? "border-white/10 bg-slate-900/70" : "border-black/[0.05]"}`}
        >
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg shadow-sm"
              style={{
                background: `linear-gradient(145deg, ${color}, ${color}cc)`,
              }}
              aria-hidden
            >
              <Sparkles size={14} className="text-white" strokeWidth={2.2} />
            </span>
            <div className="min-w-0">
              <p
                className={`truncate text-[10px] font-black uppercase tracking-[0.2em] ${heroIsDark ? "text-white/45" : "text-neutral-500"}`}
              >
                Category lens
              </p>
              <p
                className={`truncate text-xs font-bold ${heroIsDark ? "text-white/90" : "text-neutral-900"}`}
              >
                {visual.tone}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
            <span
              className={`text-[10px] font-black tabular-nums ${heroIsDark ? "text-white" : "text-neutral-900"}`}
            >
              {topicsCount} topics
            </span>
            <span
              className={`text-[9px] font-semibold ${heroIsDark ? "text-white/50" : "text-neutral-500"}`}
            >
              {guidesApprox} guides
            </span>
          </div>
        </div>

        <div className="grid gap-2.5 p-1.5 sm:p-2 lg:h-[26rem] lg:grid-cols-[minmax(0,1fr)_11.25rem] lg:grid-rows-2 xl:h-[27.5rem]">
          <div className="relative aspect-[4/5] min-h-[14rem] overflow-hidden rounded-[1.1rem] bg-neutral-900 shadow-inner ring-1 ring-black/20 sm:aspect-[3/4] sm:min-h-[17rem] lg:col-span-1 lg:row-span-2 lg:aspect-auto lg:min-h-0 lg:h-full">
            <img
              src={visual.heroImage}
              alt=""
              className="h-full w-full object-cover transition duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.02]"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-black/25 via-transparent to-black/50" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/70 to-transparent" />
            <div className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4">
              <p className="text-[9px] font-black uppercase tracking-[0.28em] text-white/70">
                Primary story
              </p>
              <p className="mt-1 line-clamp-2 text-sm font-bold leading-snug text-white drop-shadow-sm sm:text-base">
                {industry.label}
              </p>
            </div>
          </div>

          <div className="relative aspect-[16/10] min-h-[6.5rem] overflow-hidden rounded-[0.85rem] bg-neutral-900 ring-1 ring-black/10 sm:min-h-[7.5rem] lg:aspect-auto lg:min-h-0 lg:h-full lg:rounded-[0.95rem]">
            <img
              src={visual.secondaryImage}
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
            <span className="absolute bottom-2 left-2 rounded bg-black/45 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-white/90 backdrop-blur-sm">
              Context
            </span>
          </div>

          <div className="relative aspect-[16/10] min-h-[6.5rem] overflow-hidden rounded-[0.85rem] bg-neutral-900 ring-1 ring-black/10 sm:min-h-[7.5rem] lg:aspect-auto lg:min-h-0 lg:h-full lg:rounded-[0.95rem]">
            <img
              src={visual.accentImage}
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
            <span className="absolute bottom-2 left-2 rounded bg-black/45 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wider text-white/90 backdrop-blur-sm">
              Detail
            </span>
          </div>
        </div>

        <div
          className={`border-t px-3 py-3 sm:px-4 ${heroIsDark ? "border-white/10 bg-slate-900/75" : "border-black/[0.06] bg-white/90"}`}
        >
          <div className="flex items-start gap-3">
            <span
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-sm ${heroIsDark ? "ring-1 ring-white/10" : "ring-1 ring-black/[0.04]"}`}
              style={{ background: `${color}22`, color }}
              aria-hidden
            >
              <ImageIcon size={18} strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <p
                className={`text-[10px] font-black uppercase tracking-[0.22em] ${heroIsDark ? "text-white/45" : "text-neutral-500"}`}
              >
                Desk promise
              </p>
              <p
                className={`mt-1 text-xs font-semibold leading-relaxed ${heroIsDark ? "text-white/80" : "text-neutral-800"}`}
              >
                {visual.promise}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

const SubcategoriesShowcase = ({
  items,
  industryId,
  industryLabel,
  theme,
  color,
}) => {
  if (!items?.length) return null;

  const strip = theme.subcategoriesStripClass ?? "";
  const tile = theme.subcategoriesTileClass ?? "";
  const introWrap = theme.subcategoriesIntroWrapClass ?? "";
  const headingExtras = theme.subcategoriesHeadingExtras ?? "";
  const gridExtras = theme.subcategoriesGridExtras ?? "";
  const cardExtras = theme.subcategoriesCardExtras ?? "";
  const imageWrapExtras = theme.subcategoriesImageWrapExtras ?? "";
  const bodyExtras = theme.subcategoriesBodyExtras ?? "";
  const iconWrapExtras = theme.subcategoriesIconWrapExtras ?? "";

  return (
    <section
      className={`px-4 py-10 sm:px-6 lg:px-8 ${strip}`}
      aria-labelledby="subcategories-showcase-heading"
    >
      <div className="mx-auto max-w-8xl">
        <div className={`mb-6 ${introWrap || "max-w-xl"}`}>
          <p
            className={`text-xs font-bold uppercase tracking-widest ${theme.topicGridEyebrowClass}`}
          >
            Subcategories
          </p>
          <h2
            id="subcategories-showcase-heading"
            className={`mt-2 text-2xl font-black sm:text-3xl ${headingExtras || "text-gray-900"}`}
          >
            {industryLabel}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-gray-600">
            {items.length} topics — pick one to read guides and go deeper.
          </p>
        </div>

        <ul
          className={`flex snap-x snap-mandatory gap-4 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:thin] md:grid md:snap-none md:grid-cols-2 md:gap-5 md:overflow-visible lg:grid-cols-5 ${gridExtras}`}
        >
          <li className="w-2 shrink-0 md:hidden" aria-hidden />
          {items.map((item) => {
            const Icon = importIcon(item.icon);
            const showcaseImage = getSubcategoryShowcaseImage(industryId, item);

            return (
              <li
                key={`show-${item.slug}`}
                className="w-[min(82vw,17.5rem)] shrink-0 snap-center list-none md:w-auto md:shrink md:snap-none"
              >
                <Link
                  to={item.path || `/${industryId}/${item.slug}`}
                  className={`group flex h-full flex-col overflow-hidden bg-white shadow-sm transition hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900/25 ${tile} ${cardExtras || "rounded-2xl"}`}
                >
                  <div
                    className={`relative h-36 shrink-0 overflow-hidden bg-gray-100 sm:h-40 ${imageWrapExtras}`}
                  >
                    <img
                      src={showcaseImage}
                      alt=""
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
                      loading="lazy"
                    />
                  </div>
                  <div
                    className={`flex flex-1 flex-col gap-2 p-4 ${bodyExtras}`}
                  >
                    <div className="flex gap-3">
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center ${iconWrapExtras || "rounded-xl"}`}
                        style={{ backgroundColor: `${color}18` }}
                        aria-hidden
                      >
                        <Icon size={18} style={{ color }} />
                      </span>
                      <h3 className="min-w-0 text-sm font-bold leading-snug text-gray-900">
                        {item.name}
                      </h3>
                    </div>
                    <p className="line-clamp-2 text-xs leading-relaxed text-gray-600">
                      {item.description}
                    </p>
                    <span className="mt-auto inline-flex items-center gap-0.5 pt-1 text-xs font-semibold text-gray-700">
                      View topic
                      <ChevronRight
                        size={14}
                        className="shrink-0 transition group-hover:translate-x-0.5"
                        style={{ color }}
                      />
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
          <li className="w-3 shrink-0 md:hidden" aria-hidden />
        </ul>
      </div>
    </section>
  );
};

const SubcategoryCard = ({
  item,
  industryId,
  color,
  image,
  sponsor,
  cardHoverExtras,
  hubLayout,
  hubBrowseCardExtras,
}) => {
  const Icon = importIcon(item.icon);

  const hubShell = hubBrowseCardExtras?.trim()?.length
    ? `overflow-hidden ${hubBrowseCardExtras.trim()}`
    : "overflow-hidden rounded-2xl ring-1 ring-black/[0.045]";

  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: hubLayout ? -4 : -6 }}
      whileTap={{ scale: 0.99 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className={`group bg-white shadow-sm transition-all hover:shadow-lg ${
        hubLayout
          ? hubShell
          : "overflow-hidden rounded-[1.5rem] border border-black/[0.08] shadow-sm hover:border-[#FFD221]/60 hover:shadow-xl"
      } ${cardHoverExtras ?? ""}`}
    >
      <Link
        to={item.path || `/${industryId}/${item.slug}`}
        className="block h-full"
      >
        {hubLayout ? (
          <div
            className="h-1 w-full shrink-0"
            style={{ background: color }}
            aria-hidden
          />
        ) : null}
        <div
          className={`relative overflow-hidden bg-gray-100 ${hubLayout ? "h-[11.5rem] sm:h-[13rem]" : "h-40"}`}
        >
          <img
            src={image}
            alt=""
            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div
            className={`absolute inset-0 bg-gradient-to-t ${hubLayout ? "from-black/80 via-black/25 to-transparent" : "from-black/70 via-black/10 to-transparent"}`}
          />
          <div
            className={`absolute flex items-center justify-center rounded-2xl bg-white shadow-md ${hubLayout ? "left-4 top-4 h-12 w-12" : "left-4 top-4 h-11 w-11"}`}
          >
            <Icon size={hubLayout ? 22 : 20} style={{ color }} />
          </div>
          <span
            className={`absolute rounded-full bg-white/95 font-black uppercase tracking-wider text-black shadow-sm ${hubLayout ? "bottom-4 left-4 px-3 py-1.5 text-[9px]" : "bottom-4 left-4 px-3 py-1 text-[10px]"}`}
          >
            {sponsor.role}
          </span>
        </div>
        <div
          className={`p-5 ${hubLayout ? "bg-gradient-to-b from-gray-50/90 to-white" : ""}`}
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3
              className={`font-black leading-tight text-gray-950 ${hubLayout ? "text-[0.9375rem] sm:text-base" : "text-base"}`}
            >
              {item.name}
            </h3>
            <ChevronRight
              size={hubLayout ? 18 : 17}
              className={`shrink-0 transition group-hover:translate-x-1 ${
                hubLayout
                  ? "opacity-90"
                  : "text-gray-300 group-hover:text-[#FFD221]"
              }`}
              style={hubLayout ? { color } : undefined}
            />
          </div>
          <p className="line-clamp-3 text-sm leading-relaxed text-gray-500">
            {item.description}
          </p>
          <div className="mt-5 flex items-center justify-between border-t border-black/[0.06] pt-4">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-500">
              <BookOpen size={13} style={{ color }} />3 guides
            </span>
            <span className="text-xs font-black text-black">Open hub</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const HOW_IT_WORKS_STEPS = [
  {
    title: "Search or browse",
    desc: "A consumer starts with a real question, not a sales form.",
    Icon: Search,
  },
  {
    title: "Read the hub",
    desc: "They land on a provider-ready page with guides, visuals, and FAQs.",
    Icon: BookOpen,
  },
  {
    title: "Open an article",
    desc: "Blog-style content educates, builds trust, and captures intent.",
    Icon: FileText,
  },
  {
    title: "Connect",
    desc: "Registration, callback, WhatsApp, or booking routes them to a partner.",
    Icon: Link2,
  },
];

const StepBetweenMarker = ({ dark }) => (
  <motion.div
    variants={fadeUp}
    className={`flex shrink-0 items-center justify-center py-3 md:w-8 md:basis-auto md:self-center md:py-0 lg:w-9 ${dark ? "text-white/30" : "text-gray-300"}`}
    aria-hidden
  >
    <ArrowRight className="h-4 w-4 rotate-90 md:rotate-0" strokeWidth={2.35} />
  </motion.div>
);

const StepCard = ({ step, title, desc, color, dark, cardClass, Icon }) => (
  <motion.div
    variants={fadeUp}
    className={`group relative flex w-full min-w-0 flex-col md:min-h-0 md:flex-1 ${cardClass}`}
  >
    <div
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-inherit"
      aria-hidden
    >
      {!dark ? (
        <div
          className="absolute -right-8 top-16 h-[5.5rem] w-[5.5rem] rounded-full opacity-[0.085] blur-3xl sm:opacity-100"
          style={{ background: color }}
        />
      ) : (
        <div
          className="absolute -bottom-14 -right-10 h-[7rem] w-[7rem] rounded-full opacity-[0.22] blur-[44px]"
          style={{ background: color }}
        />
      )}
    </div>
    <div className="relative z-[1] flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-3 sm:gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border bg-white text-base font-black leading-none sm:h-[3.25rem] sm:w-[3.25rem] sm:text-lg ${
            dark
              ? "border-white/40 shadow-[0_0_56px_-4px_rgba(255,255,255,0.78),0_16px_60px_-16px_rgba(255,255,255,0.52),0_10px_32px_-8px_rgba(0,0,0,0.62)]"
              : "border-black/[0.06] shadow-[0_0_48px_-2px_rgba(255,255,255,1),0_10px_42px_-6px_rgba(255,255,255,1),0_24px_56px_-16px_rgba(0,0,0,0.22),0_8px_24px_-6px_rgba(0,0,0,0.14)]"
          }`}
          aria-hidden
        >
          <span className="tabular-nums" style={{ color }}>
            {step}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-3 sm:gap-3.5">
          <div
            className={`min-w-0 ${dark ? "text-white/45" : "text-gray-500"}`}
          >
            <p className="text-[9px] font-black uppercase tracking-[0.28em]">
              Step
            </p>
            <p className="mt-0.5 text-xs font-bold tabular-nums sm:text-[0.8125rem]">
              {step} <span className="font-semibold opacity-70">of</span> 4
            </p>
          </div>
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition group-hover:border-black/15 sm:h-11 sm:w-11 ${
              dark
                ? "border-white/15 bg-white/[0.07] text-white"
                : "border-black/[0.08] bg-white text-gray-900 shadow-sm"
            }`}
            style={{ color }}
            aria-hidden
          >
            <Icon size={19} strokeWidth={2.1} />
          </span>
        </div>
      </div>
      <p
        className={`mt-5 text-[0.9375rem] font-black leading-snug tracking-tight sm:mt-6 sm:text-base ${dark ? "text-white" : "text-gray-950"}`}
      >
        {title}
      </p>
      <p
        className={`mt-2 flex-1 text-sm leading-relaxed ${dark ? "text-white/65" : "text-gray-600"}`}
      >
        {desc}
      </p>
    </div>
  </motion.div>
);

const PopularQuestion = ({ question, to, color }) => (
  <Link
    to={to}
    className="group flex items-center justify-between gap-4 rounded-2xl border border-black/[0.08] bg-white px-5 py-4 shadow-sm transition hover:-translate-y-0.5 hover:border-[#FFD221]/60 hover:shadow-lg"
  >
    <span className="text-sm font-bold leading-snug text-gray-800 group-hover:text-black">
      {question}
    </span>
    <span
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition group-hover:scale-105"
      style={{ background: `${color}18`, color }}
    >
      <ArrowRight size={15} />
    </span>
  </Link>
);

/** Topic grid + search results — placed directly under the category hero. */
const TopicHubsBrowseSection = ({
  search,
  setSearch,
  filtered,
  browseHubItems,
  items,
  industryId,
  color,
  sponsor,
  theme,
}) => (
  <section
    id="category-topic-hubs"
    className="scroll-mt-24 px-4 pb-8 pt-2 sm:scroll-mt-28 sm:px-6 lg:px-8"
  >
    <div className={`mx-auto max-w-8xl ${theme.browseHubSectionClass ?? ""}`}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p
            className={`text-xs font-black uppercase tracking-[0.22em] ${theme.topicGridEyebrowClass}`}
          >
            Browse topic hubs
          </p>
          <h2 className="mt-2 text-2xl font-black text-gray-950 sm:text-3xl">
            {search.trim()
              ? `Results for "${search.trim()}"`
              : "Highlighted topic hubs"}
          </h2>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600">
            {search.trim()
              ? `${filtered.length} topic${filtered.length === 1 ? "" : "s"} match your search.${filtered.length > 8 ? " Showing the first eight." : ""}`
              : `Eight curated entry points. This category lists ${items.length} topics — use search above to filter.`}
          </p>
        </div>
        {search.trim() ? (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="rounded-full border border-black/10 bg-white px-4 py-2 text-xs font-bold text-gray-500 transition hover:text-black"
          >
            Clear search
          </button>
        ) : null}
      </div>

      <AnimatePresence mode="wait">
        {browseHubItems.length > 0 ? (
          <motion.div
            key={search.trim() || "all"}
            variants={{
              visible: { transition: { staggerChildren: 0.045 } },
            }}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4"
          >
            {browseHubItems.map((item) => (
              <SubcategoryCard
                key={item.id}
                item={item}
                industryId={industryId}
                color={color}
                sponsor={sponsor}
                image={getSubcategoryShowcaseImage(industryId, item)}
                cardHoverExtras={theme.browseCardHoverClass}
                hubBrowseCardExtras={theme.hubBrowseCardExtras}
                hubLayout
              />
            ))}
          </motion.div>
        ) : (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center rounded-3xl border border-black/[0.06] bg-white py-16 text-center"
          >
            <Search size={42} className="mb-4 text-gray-200" />
            <p className="text-lg font-black text-gray-500">No topics found</p>
            <p className="mt-1 text-sm text-gray-400">
              Try a different search term
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </section>
);

const CategoryProductsPage = () => {
  const { industryId: paramId } = useParams();
  const location = useLocation();
  const { to: ctaTo } = useSignupCta();
  const isAuthenticated = useSelector((s) => Boolean(s.infoboxx_consumer?.token));
  const scrollToTopicHubs = useCallback(() => {
    document.getElementById("category-topic-hubs")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);
  const [search, setSearch] = useState("");
  const { industries: apiIndustries, menuByIndustry: apiMenuByIndustry } = useCategories();

  const industryId = paramId ?? location.pathname.split("/").filter(Boolean)[0];
  const industries = apiIndustries.length ? apiIndustries : INDUSTRIES;
  const industry = industries.find((ind) => ind.id === industryId) ?? null;
  const apiItems = (apiMenuByIndustry[industryId] || []).map((item) => ({
    id: item.id,
    name: item.label,
    description: item.description || `Explore ${item.label}`,
    slug: item.path.split("/").pop(),
    path: item.path,
    icon: item.icon || "Sparkles",
    color: item.color || industry?.color,
  }));
  const bettingStaticItems = getItemsByIndustry("betting-gaming").map((item) => ({
    id: `bet-${item.id}`,
    name: item.name,
    description: item.description,
    slug: item.slug,
    path: `/betting-gaming/${item.slug}`,
    icon: item.icon,
    color: item.color,
  }));
  const legalStaticItems = getItemsByIndustry("legal").map((item) => ({
    id: `leg-${item.id}`,
    name: item.name,
    description: item.description,
    slug: item.slug,
    path: `/legal/${item.slug}`,
    icon: item.icon,
    color: item.color,
  }));
  const items =
    industryId === "betting-gaming"
      ? bettingStaticItems
      : industryId === "legal"
        ? legalStaticItems
        : apiItems.length
          ? apiItems
          : getItemsByIndustry(industryId);
  const industrySafe = industry
    ? {
        ...industry,
        heroQuestion:
          industry.heroQuestion ||
          `What should I know about ${industry.label}?`,
      }
    : null;
  const visual = getIndustryVisual(industryId);
  const sponsor = getSponsorForIndustry(industryId);

  const filtered = search.trim()
    ? items.filter(
        (item) =>
          item.name.toLowerCase().includes(search.toLowerCase()) ||
          item.description.toLowerCase().includes(search.toLowerCase()),
      )
    : items;

  const featuredItems = useMemo(() => items.slice(0, 3), [items]);
  const browseHubItems = useMemo(() => filtered.slice(0, 8), [filtered]);
  const popularQuestions = useMemo(() => {
    if (industryId === "betting-gaming") {
      return BETTING_GAMING_POPULAR_QUESTIONS.map((row) => ({
        question: row.question,
        to: `/${industryId}/${row.slug}`,
      }));
    }
    return items.slice(0, 5).map((item) => ({
      question: item.description.replace(/\.$/, "?"),
      to: `/${industryId}/${item.slug}`,
    }));
  }, [industryId, items]);

  if (!industrySafe) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f4ec]">
        <div className="rounded-3xl bg-white p-10 text-center shadow-xl">
          <p className="mb-3 text-4xl font-black text-gray-900">
            Category not found
          </p>
          <Link to="/" className="font-semibold text-[#c9a000] underline">
            Go home
          </Link>
        </div>
      </div>
    );
  }

  const color = industrySafe.color;
  const theme = getCategoryPageTheme(industryId);
  const narrativeDark =
    industryId === "legal" || industryId === "betting-gaming";
  const heroIsDark = narrativeDark;
  const stepSectionUsesDarkChrome = /bg-(slate|neutral)-950/.test(
    theme.stepSectionBgClass,
  );
  const howItWorksEyebrow =
    (theme.howItWorksEyebrowClass ?? "").trim() ||
    (stepSectionUsesDarkChrome ? "text-[#FFD221]" : "text-[#c9a000]");
  const howItWorksSubtitle =
    (theme.howItWorksSubtitle ?? "").trim() ||
    "Each step answers one question before asking for anything—so partners meet readers who are already oriented.";
  const howItWorksShell =
    (theme.howItWorksShellClass ?? "").trim() ||
    (stepSectionUsesDarkChrome
      ? "rounded-[2rem] border border-white/10 bg-white/[0.05] p-4 sm:p-7"
      : "rounded-[2rem] border border-black/[0.06] bg-white/75 p-4 shadow-sm backdrop-blur-sm sm:p-7");
  const howItWorksCardClass =
    (theme.howItWorksCardClass ?? "").trim() ||
    (stepSectionUsesDarkChrome
      ? "rounded-2xl border border-white/10 bg-white/[0.07] p-5 backdrop-blur-[2px] sm:p-6"
      : "rounded-2xl border border-black/[0.06] bg-white p-5 shadow-sm transition hover:border-black/[0.1] hover:shadow-md sm:p-6");
  const themedHowItWorksCta = (theme.howItWorksCtaClass ?? "").trim();
  const talkToUsBtnClass = themedHowItWorksCta
    ? `inline-flex shrink-0 items-center gap-2 rounded-full border px-5 py-3 text-sm font-bold shadow-sm transition hover:brightness-105 ${themedHowItWorksCta}`
    : `inline-flex shrink-0 items-center gap-2 rounded-full border px-5 py-3 text-sm font-bold shadow-sm transition hover:border-black ${
        stepSectionUsesDarkChrome
          ? "border-white/20 bg-white/5 text-white hover:border-white hover:bg-white/10"
          : "border-black/10 bg-white text-black hover:border-black"
      }`;
  const firstTopic = items[0] ?? { slug: "topic", name: industrySafe.label };

  return (
    <div className={theme.pageBgClass}>
      <section className="relative overflow-hidden pt-28 pb-16 sm:pb-20">
        <div className={`absolute inset-0 ${theme.heroWashClass}`} />
        <HeroPattern patternId={theme.patternId} industryId={industryId} />
        <div
          className={`absolute right-[-8rem] top-10 h-96 w-96 rounded-full blur-3xl ${theme.heroBlobRightClass}`}
          aria-hidden
        />
        <div
          className={`absolute left-[-8rem] bottom-0 h-96 w-96 rounded-full blur-3xl ${theme.heroBlobLeftClass}`}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: heroIsDark
              ? "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.1) 1px, transparent 0)"
              : "radial-gradient(circle at 1px 1px, rgba(0,0,0,0.06) 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
          aria-hidden
        />

        <div className="relative z-10 mx-auto max-w-8xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <motion.div
              initial="hidden"
              animate="visible"
              className={`lg:col-span-7 ${theme.heroInkClass}`}
            >
              <motion.div variants={fadeUp} className={theme.pillClass}>
                <Sparkles size={13} style={{ color }} />
                {visual.tone}
              </motion.div>
              <motion.h1
                variants={fadeUp}
                custom={0.04}
                className={`max-w-4xl text-4xl sm:text-6xl lg:text-7xl ${theme.fontHeadingClass} leading-[1.05]`}
              >
                {industry.label}
                <span className={`mt-2 block ${theme.headlineAccentClass}`}>
                  {theme.headlineSuffix}
                </span>
              </motion.h1>
              <motion.div
                variants={fadeUp}
                custom={0.085}
                className={`mt-7 max-w-2xl rounded-2xl border px-5 py-4 backdrop-blur-md ${
                  heroIsDark
                    ? "border-white/15 bg-white/[0.08]"
                    : "border-black/[0.08] bg-white/85 shadow-[0_20px_50px_-44px_rgba(0,0,0,0.35)]"
                }`}
              >
                <div className="flex gap-4">
                  <span
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${
                      heroIsDark
                        ? "border-white/15 bg-black/35"
                        : "border-black/[0.07] bg-white"
                    }`}
                    style={{ color }}
                    aria-hidden
                  >
                    <HelpCircle size={21} strokeWidth={2} />
                  </span>
                  <div className="min-w-0">
                    <p
                      className={`text-[10px] font-black uppercase tracking-[0.2em] ${heroIsDark ? "text-white/50" : "text-gray-500"}`}
                    >
                      Question we hear most
                    </p>
                    <p
                      className={`mt-1 text-[1.0625rem] font-bold leading-snug sm:text-lg ${heroIsDark ? "text-white" : "text-gray-950"}`}
                    >
                      “{industrySafe.heroQuestion}”
                    </p>
                  </div>
                </div>
              </motion.div>
              <motion.p
                variants={fadeUp}
                custom={0.12}
                className={`mt-7 max-w-2xl text-base leading-8 sm:text-lg ${theme.heroMutedClass}`}
              >
                {visual.promise} Browse expert-backed topics, read provider
                content, and decide when you are ready to connect.
              </motion.p>

              <motion.div
                variants={fadeUp}
                custom={0.155}
                className={`mt-8 max-w-2xl ${theme.searchShellClass}`}
              >
                <div className="relative">
                  <Search
                    size={18}
                    className={`pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 ${heroIsDark ? "text-slate-500" : "text-gray-400"}`}
                  />
                  <input
                    type="text"
                    placeholder={`Search ${industrySafe.label} topics...`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={`w-full rounded-2xl border-0 py-4 pl-11 pr-5 text-sm font-semibold focus:outline-none focus:ring-2 ${theme.searchInputBgClass} ${theme.searchRingClass} ${heroIsDark ? "placeholder:text-slate-500" : "text-gray-900 placeholder:text-gray-400"}`}
                  />
                </div>
                {search.trim() ? (
                  <p
                    className={`mt-3 text-sm font-semibold ${heroIsDark ? "text-white/80" : "text-gray-700"}`}
                    aria-live="polite"
                  >
                    {filtered.length} topic{filtered.length === 1 ? "" : "s"} found — see results below
                  </p>
                ) : null}
              </motion.div>
            </motion.div>

            <div className="relative lg:col-span-5">
              <div
                className={`pointer-events-none absolute -bottom-14 left-6 right-6 hidden h-24 rounded-full blur-3xl lg:block ${heroIsDark ? "opacity-90" : ""}`}
                style={
                  heroIsDark
                    ? { background: `${color}40` }
                    : { background: "rgba(0,0,0,0.065)" }
                }
                aria-hidden
              />
              <ImageCollage
                visual={visual}
                color={color}
                industry={industrySafe}
                frameClassName={theme.collageRingClass}
                topicsCount={items.length}
                guidesApprox={`${Math.max(items.length * 3, 3)}+`}
                heroIsDark={heroIsDark}
              />
            </div>
          </div>
        </div>
      </section>

      <div className={theme.mainSurfaceClass}>
        <TopicHubsBrowseSection
          search={search}
          setSearch={setSearch}
          filtered={filtered}
          browseHubItems={browseHubItems}
          items={items}
          industryId={industryId}
          color={color}
          sponsor={sponsor}
          theme={theme}
        />

        {/* About this category */}
        <div className="px-4 pt-2 sm:px-6 lg:px-8">
          <CategoryStoryBand
            theme={theme}
            industryLabel={industrySafe.label}
            color={color}
            narrativeDark={narrativeDark}
          />
        </div>

        {industryId === "betting-gaming" ? (
          <BettingGamingCategoryPageSections industryId={industryId} />
        ) : null}

        {/* Featured partner */}
        <section className="relative z-10 px-4 pb-10 pt-2 sm:px-6 lg:px-8">
          <div
            className={`mx-auto max-w-8xl border p-5 sm:p-6 ${theme.partnerStripClass}`}
          >
            <div className="grid gap-5 lg:grid-cols-[1.1fr_1.4fr_auto] lg:items-center">
              <div>
                <p
                  className={`text-xs font-black uppercase tracking-[0.2em] ${theme.partnerEyebrowClass}`}
                >
                  Featured partner
                </p>
                <h2 className="mt-2 text-2xl font-black">{sponsor.name}</h2>
                <p className="mt-2 text-sm leading-relaxed opacity-70">
                  {sponsor.trustLine}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {featuredItems.map((item) => (
                  <Link
                    key={item.slug}
                    to={item.path || `/${industryId}/${item.slug}`}
                    className="rounded-2xl border border-white/10 bg-white/10 p-4 transition hover:bg-white/15"
                  >
                    <p className="line-clamp-2 text-sm font-bold">
                      {item.name}
                    </p>
                    <p className="mt-2 text-xs text-white/45">
                      Open provider hub
                    </p>
                  </Link>
                ))}
              </div>
              {isAuthenticated ? (
                <button
                  type="button"
                  onClick={scrollToTopicHubs}
                  className="group inline-flex items-center justify-center gap-2.5 rounded-2xl bg-[#FFD221] px-6 py-4 text-sm font-black text-black shadow-[0_14px_40px_-18px_rgba(234,179,8,0.65)] transition hover:bg-[#ffdf4a] hover:shadow-[0_18px_44px_-14px_rgba(234,179,8,0.55)]"
                >
                  <LayoutGrid size={17} strokeWidth={2.25} aria-hidden />
                  Explore topic hubs
                  <ArrowRight
                    size={15}
                    className="transition-transform group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </button>
              ) : (
                <Link
                  to={ctaTo}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#FFD221] px-6 py-4 text-sm font-black text-black transition hover:bg-[#ffdf4a]"
                >
                  Register Free
                  <ArrowRight size={15} />
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* Subcategories showcase */}
        <SubcategoriesShowcase
          items={items}
          industryId={industryId}
          industryLabel={industrySafe.label}
          theme={theme}
          color={color}
        />

        {/* Popular questions */}
        <section className="px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-8xl gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="rounded-[2rem] bg-white p-6 shadow-sm ring-1 ring-black/[0.06] sm:p-8"
            >
              <div
                className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-black uppercase tracking-[0.18em]"
                style={{ background: `${color}18`, color }}
              >
                <MessageCircle size={13} />
                Popular questions
              </div>
              <h2 className="text-3xl font-black text-gray-950">
                Start where the consumer already is.
              </h2>
              <div className="mt-6 grid gap-3">
                {popularQuestions.map((item) => (
                  <PopularQuestion key={item.to} {...item} color={color} />
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="w-full overflow-hidden rounded-[2rem] bg-gray-950 text-white shadow-xl ring-1 ring-black/30 lg:self-start"
            >
              <div className="relative h-44 shrink-0 sm:h-52 lg:h-56">
                <img
                  src={visual.secondaryImage}
                  alt=""
                  className="h-full w-full object-cover opacity-75"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/55 to-gray-950/20" />
                <div className="absolute bottom-4 left-4 right-4 sm:bottom-5 sm:left-5 sm:right-5">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-[#FFD221] backdrop-blur sm:text-xs">
                    <Play size={12} />
                    Preview journey
                  </div>
                  <h3 className="mt-2 text-lg font-black leading-tight sm:mt-3 sm:text-[1.35rem] lg:text-xl">
                    {isAuthenticated
                      ? "Watch, read, and connect when you're ready"
                      : "Watch, read, register, connect"}
                  </h3>
                </div>
              </div>
              <div className="flex flex-col gap-5 p-6 sm:gap-6 sm:p-8">
                <div className="grid gap-3 sm:grid-cols-3 sm:gap-3">
                  {[
                    ["CMS", "Replace images and copy"],
                    ["Leads", sponsor.action],
                    [
                      "Articles",
                      `${getTopicArticles(industryId, firstTopic).length} ready`,
                    ],
                  ].map(([title, desc]) => (
                    <div
                      key={title}
                      className="rounded-xl border border-white/12 bg-gradient-to-b from-white/[0.07] to-white/[0.02] px-3 py-4 shadow-inner shadow-black/40"
                    >
                      <p className="text-xs font-black sm:text-sm">{title}</p>
                      <p className="mt-1 text-[10px] leading-snug text-white/55 sm:text-[11px]">
                        {desc}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2.5 border-t border-white/10 pt-5">
                  {["Brand-safe CMS", "Routed leads", "Guided articles"].map(
                    (label) => (
                      <span
                        key={label}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/12 bg-white/[0.05] px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide text-white/75"
                      >
                        <CheckCircle2
                          size={12}
                          className="shrink-0 text-[#FFD221]"
                          aria-hidden
                        />
                        {label}
                      </span>
                    ),
                  )}
                </div>

                <div className="flex flex-col gap-4 rounded-xl border border-[#FFD221]/30 bg-gradient-to-br from-[#FFD221]/[0.14] via-white/[0.04] to-transparent p-5 sm:p-6">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFD221]/20 text-[#FFD221] ring-1 ring-[#FFD221]/35">
                      <Users size={18} strokeWidth={2} aria-hidden />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black uppercase tracking-[0.26em] text-[#FFD221]">
                        Become a partner
                      </p>
                      <p className="mt-1 text-base font-black leading-snug text-white sm:text-lg">
                        Want to become a partner?
                      </p>
                      <p className="mt-2 text-[11px] leading-relaxed text-white/60 sm:text-xs">
                        Complete partner onboarding to get verified, publish
                        category content, and receive qualified requests from
                        readers.
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/partners/onboard"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#FFD221] px-5 py-3 text-xs font-black text-black shadow-lg shadow-[#FFD221]/25 transition hover:bg-[#ffdf58]"
                  >
                    Start partner onboarding
                    <ArrowRight size={14} aria-hidden />
                  </Link>
                  <p className="text-center text-[10px] text-white/40">
                    Featured partner example:{" "}
                    <span className="font-semibold text-white/55">
                      {sponsor.name}
                    </span>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* How it works */}
        <section
          className={`px-4 py-16 sm:px-6 lg:px-8 ${theme.stepSectionBgClass}`}
        >
          <div className="mx-auto max-w-8xl">
            <div className="relative mb-10 pb-10 sm:flex sm:flex-wrap sm:items-start sm:justify-between sm:gap-x-10 sm:gap-y-10 sm:border-0 sm:pb-2">
              <div
                className={`relative max-w-2xl border-b pb-10 sm:flex-1 sm:border-0 sm:pb-0 ${stepSectionUsesDarkChrome ? "border-white/12" : "border-black/[0.08]"}`}
              >
                <span
                  className={`mb-5 block h-[3px] max-w-[5.25rem] rounded-full sm:mb-6 ${
                    stepSectionUsesDarkChrome
                      ? "bg-[#FFD221]/85"
                      : "bg-gradient-to-r from-gray-950 via-gray-500 to-transparent"
                  }`}
                  aria-hidden
                />
                <p
                  className={`text-xs font-black uppercase tracking-[0.22em] ${howItWorksEyebrow}`}
                >
                  How it works
                </p>
                <h2
                  className={`mt-2 text-balance text-3xl font-black sm:text-4xl ${stepSectionUsesDarkChrome ? "text-white" : "text-gray-950"}`}
                >
                  From question to qualified action
                </h2>
                <p
                  className={`mt-3 max-w-xl text-pretty text-sm leading-relaxed sm:text-[0.9375rem] ${
                    stepSectionUsesDarkChrome
                      ? "text-white/60"
                      : "text-gray-600"
                  }`}
                >
                  {howItWorksSubtitle}
                </p>
              </div>
              <Link to="/contact" className={`sm:mt-2 ${talkToUsBtnClass}`}>
                Talk to us
                <ArrowRight size={14} aria-hidden />
              </Link>
            </div>

            <div className={`${howItWorksShell}`}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={{
                  visible: { transition: { staggerChildren: 0.07 } },
                }}
                className="flex flex-col gap-y-5 md:flex-row md:items-stretch md:gap-x-2 md:gap-y-0 lg:gap-x-3"
              >
                {HOW_IT_WORKS_STEPS.flatMap((row, idx) => {
                  const fragments = [
                    <StepCard
                      key={`step-${row.title}`}
                      step={idx + 1}
                      title={row.title}
                      desc={row.desc}
                      color={color}
                      dark={stepSectionUsesDarkChrome}
                      cardClass={howItWorksCardClass}
                      Icon={row.Icon}
                    />,
                  ];
                  if (idx < HOW_IT_WORKS_STEPS.length - 1) {
                    fragments.push(
                      <StepBetweenMarker
                        key={`between-${idx}`}
                        dark={stepSectionUsesDarkChrome}
                      />,
                    );
                  }
                  return fragments;
                })}
              </motion.div>
              <div
                className={`mt-8 flex flex-col gap-4 border-t pt-7 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between ${
                  stepSectionUsesDarkChrome
                    ? "border-white/10"
                    : "border-black/[0.06]"
                }`}
              >
                <span
                  className={`inline-flex items-center gap-3 text-[11px] font-black uppercase tracking-[0.22em] ${
                    stepSectionUsesDarkChrome
                      ? "text-white/45"
                      : "text-gray-500"
                  }`}
                >
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                      stepSectionUsesDarkChrome
                        ? "bg-white/10"
                        : "bg-black/[0.04]"
                    }`}
                    style={{ color }}
                    aria-hidden
                  >
                    <TrendingUp size={18} strokeWidth={2.1} />
                  </span>
                  Reader-native flow · optimised for skim-to-commit
                </span>
                <span
                  className={`inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.22em] ${
                    stepSectionUsesDarkChrome
                      ? "text-white/45"
                      : "text-gray-500"
                  }`}
                >
                  <Clock size={16} strokeWidth={2.1} aria-hidden />
                  ~Three minute journey
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Join thousands of Ghanaians */}
        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div
            className={`mx-auto max-w-8xl overflow-hidden rounded-[2rem] bg-black text-white shadow-2xl ${theme.ctaGlowClass}`}
          >
            <div className="grid lg:grid-cols-[1fr_0.85fr]">
              <div className="p-8 sm:p-10 lg:p-12">
                {isAuthenticated ? (
                  <>
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#FFD221]/18 px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-[#FFD221] ring-1 ring-[#FFD221]/25">
                      <CheckCircle2 size={13} strokeWidth={2.5} />
                      Signed in — full access
                    </div>
                    <h2 className="max-w-2xl text-3xl font-black leading-[1.08] sm:text-5xl">
                      Pick a hub.
                      <span className="mt-1 block text-white/88 sm:mt-2">
                        We saved you the signup sermon.
                      </span>
                    </h2>
                    <p className="mt-5 max-w-xl text-sm leading-7 text-white/60">
                      Every topic below opens the full layout—overview, articles, and partner context—
                      tuned for{" "}
                      <span className="font-semibold text-white/85">{industrySafe.label}</span>.
                      Scroll to the grid or jump straight from the featured row above.
                    </p>
                    <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                      {[
                        "Deep-dive hubs with guides you can skim in minutes",
                        "Verified partners when you want human follow-through",
                        "One account across every category you care about",
                        "Walkthrough still available if you prefer a guided tour",
                      ].map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 text-sm text-white/80"
                        >
                          <CheckCircle2
                            size={16}
                            className="mt-0.5 shrink-0"
                            style={{ color }}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                      <button
                        type="button"
                        onClick={scrollToTopicHubs}
                        className="group inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-sm font-black text-black transition hover:scale-[1.02]"
                        style={{ background: "#FFD221" }}
                      >
                        <LayoutGrid size={16} strokeWidth={2.25} aria-hidden />
                        Explore topic hubs
                        <ArrowRight
                          size={15}
                          className="transition-transform group-hover:translate-x-0.5"
                          aria-hidden
                        />
                      </button>
                      <Link
                        to="/profile"
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/22 px-6 py-4 text-sm font-bold text-white transition hover:border-white/35 hover:bg-white/10"
                      >
                        <UserCircle size={17} strokeWidth={2} aria-hidden />
                        Your profile
                      </Link>
                      <Link
                        to="/contact"
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/[0.06] px-6 py-4 text-sm font-bold text-white/92 transition hover:bg-white/12"
                      >
                        <Clock size={15} strokeWidth={2} aria-hidden />
                        Book a walkthrough
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-[#FFD221]/15 px-3 py-1 text-xs font-black uppercase tracking-[0.18em] text-[#FFD221]">
                      <Users size={13} />
                      Join thousands of Ghanaians
                    </div>
                    <h2 className="max-w-2xl text-3xl font-black leading-tight sm:text-5xl">
                      Get personalised expert guidance for free.
                    </h2>
                    <p className="mt-5 max-w-xl text-sm leading-7 text-white/60">
                      Register to unlock personalised content recommendations,
                      direct expert access, and SMS updates around{" "}
                      {industrySafe.label.toLowerCase()}.
                    </p>
                    <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                      {[
                        "Free account, no credit card required",
                        "Personalised content matched to your interests",
                        "Direct access to verified expert partners",
                        "SMS tips and updates on your topics",
                      ].map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2 text-sm text-white/80"
                        >
                          <CheckCircle2
                            size={16}
                            className="mt-0.5 shrink-0"
                            style={{ color }}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                      <Link
                        to={ctaTo}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-sm font-black text-black transition hover:scale-[1.02]"
                        style={{ background: "#FFD221" }}
                      >
                        Register Free
                        <ArrowRight size={15} />
                      </Link>
                      <Link
                        to="/contact"
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 px-6 py-4 text-sm font-bold text-white transition hover:bg-white/10"
                      >
                        <Clock size={15} />
                        Book a walkthrough
                      </Link>
                    </div>
                  </>
                )}
              </div>
              <div className="relative min-h-[22rem]">
                <img
                  src={visual.accentImage}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover opacity-75"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/35 to-transparent lg:bg-gradient-to-l" />
                <div className="absolute bottom-6 left-6 right-6 rounded-3xl border border-white/15 bg-white/10 p-5 backdrop-blur">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#FFD221] text-black">
                      <TrendingUp size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-black">
                        Built for monetizable journeys
                      </p>
                      <p className="mt-1 text-xs text-white/55">
                        Content first. Qualified provider action next.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Explore other categories */}
        <section className="px-4 pb-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-8xl">
            <h2 className="mb-6 text-xl font-black text-gray-900">
              Explore other categories
            </h2>
            <div className="flex flex-wrap gap-3">
              {industries
                .filter((ind) => ind.id !== industryId)
                .map((ind) => {
                  const IndIcon = importIcon(ind.icon);
                  return (
                    <Link
                      key={ind.id}
                      to={ind.path || `/${ind.id}`}
                      className={`inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-all ${theme.exploreChipHoverClass}`}
                    >
                      <IndIcon size={14} style={{ color: ind.color }} />
                      {ind.label}
                    </Link>
                  );
                })}
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default CategoryProductsPage;
